import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import { siteEmail, siteName, siteSlogan } from "@/mocks/guawa";
import useSeo from "@/hooks/useSeo";

const values = [
  { icon: "ri-scales-3-line", title: "尊重事实", desc: "讨论建立在公开信息与核实基础上，拒绝以讹传讹。" },
  { icon: "ri-emotion-happy-line", title: "友善表达", desc: "欢迎观点碰撞，反对人身攻击与带节奏的恶意引战。" },
  { icon: "ri-user-heart-line", title: "保护隐私", desc: "严格避免散播与热点无关的个人隐私信息。" },
  { icon: "ri-heart-3-line", title: "持续热爱", desc: "保持对新鲜事的好奇，也保持对真相的耐心。" },
];

const timeline = [
  { year: "2024", title: "一个小小的讨论场", desc: "最初只是几位爱围观的朋友建的讨论组，聊聊影视与圈内趣事。" },
  { year: "2025", title: "栏目化与分类上线", desc: "陆续开设娱乐、影视、科技、职场等栏目，围观逐渐有了章法。" },
  { year: "2026", title: "成为吃瓜群众聚集地", desc: "积累了越来越多理性又有趣的瓜友，热议专区与投稿通道正式开放。" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 今日曝瓜 | 平台定位与发展理念",
    description:
      "了解今日曝瓜的平台定位、内容原则与互动守则。我们致力于打造尊重事实、友善表达、保护隐私的理性吃瓜社区，看透每一个热点事件。",
  });

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />

      {/* 页头 */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -right-16 -top-16 h-72 w-72 rounded-full bg-primary-100/70 blur-3xl"></div>
          <div className="absolute -left-16 bottom-0 h-72 w-72 rounded-full bg-accent-100/70 blur-3xl"></div>
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-12 md:px-6">
          <nav className="flex items-center gap-1.5 text-xs text-ink-400" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="font-medium text-ink-600 whitespace-nowrap">关于我们</span>
          </nav>
          <h1 className="mt-6 max-w-3xl text-3xl font-extrabold leading-tight tracking-tight text-ink-900 md:text-4xl">
            我们想把「围观」这件事，做得理性又有趣
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink-500">
            {siteName} 是一群爱围观、爱思考的年轻人共同维护的热点讨论社区。我们把散落在各处的热点事件收拢成一个个围观现场，邀请你一起看透事件，也看懂人心。
          </p>
        </div>
      </section>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 md:px-6">
        {/* 品牌定位 */}
        <section className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <div className="rounded-2xl border border-cream-200 bg-white p-7">
            <h2 className="flex items-center gap-2 text-xl font-extrabold text-ink-900">
              <i className="ri-focus-2-line text-primary-500"></i> 我们是谁
            </h2>
            <p className="mt-4 text-sm leading-8 text-ink-600">
              {siteName} 的定位，是普通网友的「热点聚合器」与「理性讨论场」。我们不做情绪的放大器，只做信息的整理者——把来龙去脉讲清楚，把不同视角摆出来，剩下的判断交给屏幕前的你。
            </p>
            <p className="mt-3 text-sm leading-8 text-ink-600">
              相比单纯搬运热搜，我们更希望提供一个让观点自由又文明地碰撞的空间。评论区里的一条神回复、一个清醒的角度，往往比事件本身更值得回味。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/topics"
                className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                逛逛热议专区 <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-cream-300 bg-white px-4 py-2 text-sm font-semibold text-ink-700 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap"
              >
                <i className="ri-edit-2-line"></i> 投稿或反馈
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-2xl border border-cream-200 bg-white p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-100 text-2xl text-accent-600">
                  <i className={v.icon}></i>
                </span>
                <h3 className="mt-3 text-sm font-bold text-ink-900">{v.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-ink-500">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 成长脉络 */}
        <section className="mt-16">
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">成长脉络</span>
              <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-ink-900">一路走来的围观现场</h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-ink-500">从一个兴趣小组成长为现在的 {siteName}，我们一直记得最初的单纯快乐。</p>
          </div>

          <ol className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {timeline.map((t, i) => (
              <li key={t.year} className="relative rounded-2xl border border-cream-200 bg-white p-6">
                <span className="absolute -top-3 left-6 flex items-center gap-2 text-xs font-bold text-accent-600">
                  <i className="ri-flag-2-fill"></i> {t.year}
                </span>
                <p className="pt-3 text-xs font-semibold text-primary-600">STEP {i + 1}</p>
                <h3 className="mt-1 text-lg font-bold text-ink-900">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-500">{t.desc}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* 内容原则 */}
        <section className="mt-16 rounded-3xl bg-ink-800 p-7 text-cream-100 md:p-10">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-widest text-accent-400">内容原则</span>
              <h2 className="mt-2 text-2xl font-extrabold text-white">我们希望每一场围观，都经得起推敲</h2>
              <p className="mt-3 text-sm leading-7 text-cream-200/80">
                不制造焦虑、不消费受害者、不传播未经核实的信息。热门可以追，但底线要守住——这是我们和所有瓜友共同的默契。
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-accent-500 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-accent-600 whitespace-nowrap"
            >
              <i className="ri-chat-check-line"></i> 遇到问题随时反馈
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            {["不传谣不造谣", "尊重事实与隐私", "倡导友善表达"].map((t) => (
              <span key={t} className="flex items-center gap-2 rounded-xl bg-ink-700/60 px-4 py-3 text-cream-100">
                <i className="ri-checkbox-circle-line text-accent-400"></i> {t}
              </span>
            ))}
          </div>
        </section>

        {/* 口碑 & 联系 */}
        <section className="mt-16 mb-14 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">联系我们</span>
          <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-ink-900 md:text-3xl">想了解或加入我们？</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-ink-500">
            {siteSlogan}。无论是投稿、建议还是商务合作，欢迎通过下列方式与我们取得联系。
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={`mailto:${siteEmail}`}
              className="inline-flex items-center gap-2 rounded-full border border-cream-300 bg-white px-5 py-2.5 text-sm font-semibold text-ink-700 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap"
            >
              <i className="ri-mail-line text-primary-600"></i> {siteEmail}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              前往联系 / 投稿页 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      {/* AboutPage 结构化数据 */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于" + siteName,
            url: "https://ebimall.cn/about",
            inLanguage: "zh-CN",
            mainEntity: {
              "@type": "Organization",
              name: siteName,
              slogan: siteSlogan,
              email: siteEmail,
              foundingDate: "2024",
            },
          }),
        }}
      />

      <Footer />
      <BackToTop />
    </div>
  );
}